import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useState } from "react";
import { Check, Loader2, Phone, Clock, ArrowRight, Globe, Paperclip, MapPin } from "lucide-react";
import { supabase } from "@/lib/supabase";

// ============================================================================
// CustomerRequestsPanel (PORTAL-1) — surfaces self-service requests from the
// customer portal on Today so they NEVER fall in a hole. New service requests,
// plan signups and questions appear here for the office to act on + clear.
// Self-hides when there's nothing new (a quiet morning reads short).
// ============================================================================

interface Req {
  id: number; custno: string; kind: string;
  message: string | null; phone: string | null; preferred_time: string | null;
  request_type: string | null; urgency: string | null; symptoms: string | null;
  loc_label: string | null;
  media: { path: string; name?: string }[] | null;
  created_at: string; name?: string | null;
  /** NO-DUP guard (Seth 2026-08-26: "I want to make sure we don't have
   *  duplicate tickets"): what this customer ALREADY has open, so the office
   *  books the existing ticket instead of minting a twin. */
  openTickets?: number;
  bookedVisit?: string | null; // next scheduled Pegasus visit date, if any
  /** FILES-CALL-01: set once the office books it — the photos were copied onto that call. */
  dispatch_ref?: string | null;
}

async function openMedia(path: string) {
  const { data } = await supabase.storage.from("portal-media").createSignedUrl(path, 3600);
  if (data?.signedUrl) window.open(data.signedUrl, "_blank", "noopener");
}
function urgencyCls(u: string): string {
  if (/emerg/i.test(u)) return "bg-red-50 text-red-700";
  if (/week/i.test(u)) return "bg-amber-50 text-amber-700";
  return "bg-emerald-50 text-emerald-700";
}

const KIND: Record<string, { label: string; cls: string }> = {
  service: { label: "Service Request", cls: "bg-brand-50 text-brand-700" },
  renewal: { label: "Plan Signup / Renewal", cls: "bg-emerald-50 text-emerald-700" },
  question: { label: "Question", cls: "bg-amber-50 text-amber-700" },
};

function prettyName(full: string | null | undefined, fallback: string): string {
  if (!full) return fallback;
  return full.replace(/\s+\d{4,}$/, "").replace(/^([^,]+),\s*(.+)$/, "$2 $1").trim() || fallback;
}
function ago(iso: string): string {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function useNewRequests() {
  return useQuery({
    queryKey: ["portal_requests_new"],
    refetchInterval: 60_000,
    queryFn: async (): Promise<Req[]> => {
      const { data, error } = await supabase
        .from("portal_request")
        .select("id, custno, kind, message, phone, preferred_time, request_type, urgency, symptoms, loc_label, media, created_at, dispatch_ref")
        .eq("status", "new")
        .order("created_at", { ascending: false });
      if (error) throw error;
      const rows = (data ?? []) as Req[];
      const custnos = [...new Set(rows.map((r) => r.custno))];
      if (custnos.length) {
        const today = new Date().toISOString().slice(0, 10);
        const [{ data: cs }, { data: open }] = await Promise.all([
          supabase.from("mirror_customer").select("custno, fullname").in("custno", custnos),
          supabase.from("pegasus_dispatch")
            .select("custno, status, scheduled_date")
            .in("custno", custnos)
            .in("status", ["pending_schedule", "scheduled"])
            .is("deleted_at", null),
        ]);
        const m = new Map((cs ?? []).map((c: { custno: string; fullname: string | null }) => [c.custno, c.fullname]));
        const pend = new Map<string, number>();
        const booked = new Map<string, string>();
        for (const t of (open ?? []) as { custno: string; status: string; scheduled_date: string | null }[]) {
          if (t.status === "pending_schedule") pend.set(t.custno, (pend.get(t.custno) ?? 0) + 1);
          else if (t.scheduled_date && t.scheduled_date >= today) {
            const cur = booked.get(t.custno);
            if (!cur || t.scheduled_date < cur) booked.set(t.custno, t.scheduled_date);
          }
        }
        rows.forEach((r) => {
          r.name = m.get(r.custno) ?? null;
          r.openTickets = pend.get(r.custno) ?? 0;
          r.bookedVisit = booked.get(r.custno) ?? null;
        });
      }
      return rows;
    },
  });
}

export function CustomerRequestsPanel() {
  const { data } = useNewRequests();
  const qc = useQueryClient();
  const [busy, setBusy] = useState<number | null>(null);

  const rows = data ?? [];
  if (rows.length === 0) return null; // self-hide

  async function markHandled(id: number) {
    setBusy(id);
    await supabase.from("portal_request").update({ status: "handled" }).eq("id", id);
    await qc.invalidateQueries({ queryKey: ["portal_requests_new"] });
    setBusy(null);
  }

  return (
    <section>
      <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
        <Globe className="h-3.5 w-3.5 text-brand-500" />
        Customer Requests
        <span className="rounded-full bg-brand-600 px-1.5 text-[10px] font-bold text-white tabular-nums">{rows.length}</span>
      </h3>
      <div className="space-y-2">
        {rows.map((r) => {
          const k = KIND[r.kind] ?? KIND.service;
          return (
            <div key={r.id} className="rounded-xl border border-hairline bg-white p-3.5 shadow-e1">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link to={`/customers/${r.custno}`} className="text-sm font-semibold text-slate-900 hover:text-brand-700 hover:underline">
                      {prettyName(r.name, `#${r.custno}`)}
                    </Link>
                    {r.request_type
                      ? <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[11px] font-semibold text-white">{r.request_type}</span>
                      : <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${k.cls}`}>{k.label}</span>}
                    {r.urgency && <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${urgencyCls(r.urgency)}`}>{r.urgency}</span>}
                    {r.dispatch_ref && (
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700" title="Booked — the photos the customer sent are on the call">
                        Booked · photos on the call
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1 text-[11px] text-slate-400"><Clock className="h-3 w-3" />{ago(r.created_at)}</span>
                  </div>
                  {r.loc_label && <p className="mt-1 inline-flex items-center gap-1 text-[12px] font-medium text-slate-600"><MapPin className="h-3 w-3 text-slate-400" />{r.loc_label}</p>}
                  {((r.openTickets ?? 0) > 0 || r.bookedVisit) && (
                    <p className="mt-1 text-[12px] font-medium text-amber-700">
                      ⚠ {r.bookedVisit
                        ? `Already has a visit booked ${new Date(r.bookedVisit + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
                        : `Already has ${r.openTickets} open ticket${(r.openTickets ?? 0) === 1 ? "" : "s"}`}
                      {" — open the record and work that one instead of creating a duplicate."}
                    </p>
                  )}
                  {r.symptoms && <p className="mt-1.5 text-sm font-semibold text-slate-800">{r.symptoms}</p>}
                  {r.message && <p className="mt-0.5 text-sm text-slate-600">{r.message}</p>}
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-slate-500">
                    {r.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{r.phone}</span>}
                    {r.preferred_time && <span>Prefers: {r.preferred_time}</span>}
                  </div>
                  {r.media && r.media.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {r.media.map((m, i) => (
                        <button key={i} onClick={() => openMedia(m.path)} className="inline-flex items-center gap-1 rounded-md bg-brand-50 px-2 py-1 text-[11px] font-medium text-brand-700 transition-colors hover:bg-brand-100" title={m.name || "Open attachment"}>
                          <Paperclip className="h-3 w-3" /> View {i + 1}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => markHandled(r.id)}
                  disabled={busy === r.id}
                  className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-hairline bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-emerald-50 hover:text-emerald-700 disabled:opacity-50"
                  title="Mark as handled"
                >
                  {busy === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                  Handled
                </button>
              </div>
              <Link to={`/customers/${r.custno}`} className="mt-2 inline-flex items-center gap-1 text-[12px] font-medium text-brand-600 hover:text-brand-700">
                Open customer to book <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          );
        })}
      </div>
    </section>
  );
}
